import type { PapiDeviation } from '@wix/da-papi-types';
import { useContext } from 'react';
import isArray from 'lodash/isArray';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { getAdoptableBuyerElementGroupBase } from '../../sharedBases/adoptableBuyer';
import { getAdoptableSellerElementGroupBase } from '../../sharedBases/adoptableSeller';
import { useDeviationAttributes } from '../../../../DeviationViews/hooks/useDeviationAttributes';
import { isSafeForMuroAndVideoCreator, useWindowBreakpoint } from '../../utils';
import { ElementGroup, WindowBreakpoint } from '../../types';
import {
  NOT_INCLUDED_VISIBILITY_GROUP,
  StashDeviationElementPlacement,
  StashDeviationElementPlacementMap,
} from './types';
import type { DeviationAttributes } from '../../../types';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';

/**
 * A collection of values that help disparate elements decide their {@link StashDeviationElementPlacement}.
 */
interface DeterminationContext {
  deviation: PapiDeviation;
  deviationAttributes: DeviationAttributes;
  isLoggedIn: boolean;
  isMobile: boolean;
  isAuthor: boolean;
  windowBreakpoint: WindowBreakpoint;
  elementGroups: StashDeviationElementPlacementMap['elementGroups'];
  flags: string[];
}

/**
 * A set of common checks against the adoptable element groups that other controls use to collapse.
 */
function shouldCollapseForAdoptableElementGroups(
  determinationContext: DeterminationContext
) {
  const {
    elementGroups: { adoptableBuyer, adoptableSeller },
    windowBreakpoint,
  } = determinationContext;
  return (
    // If the adoptable buyer group has a couple elements, there isn't enough room. Signal that we should collapse this control.
    (adoptableBuyer.groupPlacement ===
      StashDeviationElementPlacement.PrimaryBar &&
      adoptableBuyer.countOnBar >= 2) ||
    // On smaller desktop screens, we'll signal to collapse this control if either adoptable-related element groups are active. Regardless
    // of the number of elements visible from each.
    ((adoptableBuyer.groupPlacement ===
      StashDeviationElementPlacement.PrimaryBar ||
      adoptableSeller.groupPlacement ===
        StashDeviationElementPlacement.PrimaryBar) &&
      windowBreakpoint === WindowBreakpoint.DesktopSmallSize)
  );
}

function buildElementGroup<
  T extends ElementGroup<StashDeviationElementPlacement>,
>(groupValues: Omit<T, 'countOnBar' | 'groupPlacement'> | false): T {
  if (!groupValues) {
    return NOT_INCLUDED_VISIBILITY_GROUP as T;
  }

  const countOnBar = Object.values(groupValues).filter(
    isVisible => isVisible
  ).length;

  let groupPlacement = StashDeviationElementPlacement.NotIncluded;
  if (countOnBar > 0) {
    groupPlacement = StashDeviationElementPlacement.PrimaryBar;
  }

  return {
    groupPlacement,
    countOnBar,
    ...groupValues,
  } as T;
}

/**
 * Returns an object that maps ActionBar elements to their {@link StashDeviationElementPlacement}.
 * (e.g. is this element on the bar? collapsed into the more menu? completely absent?)
 */
export function useStashElementPlacementMap(
  deviation: PapiDeviation
): StashDeviationElementPlacementMap {
  const windowBreakpoint = useWindowBreakpoint();
  const isAuthor = useIsCurrentUserDeviationAuthor(deviation);
  const isMobile = useContext(MobileContext);
  const { isLoggedIn } = useContext(SessionContext);
  const flags = useEnabledFlagList();
  const isOwner = !!deviation.extended?.adoptable?.isOwner;
  const deviationAttributes = useDeviationAttributes(deviation, isAuthor);

  // These are collections of buttons. They can take up large amounts of space on the action bar,
  // so we use this object to determine if other more general controls should be removed/collapsed.
  const elementGroups: StashDeviationElementPlacementMap['elementGroups'] = {
    adoptableBuyer: buildElementGroup(
      getAdoptableBuyerElementGroupBase(deviation, isMobile, isOwner)
    ),
    adoptableSeller: buildElementGroup(
      getAdoptableSellerElementGroupBase(deviation, isMobile, isOwner)
    ),
  };

  const determinationContext: DeterminationContext = {
    deviation,
    isAuthor,
    isMobile,
    isLoggedIn,
    deviationAttributes,
    windowBreakpoint,
    elementGroups,
    flags,
  };

  const placementMap: StashDeviationElementPlacementMap = {
    premiumDownload: getPremiumDownloadPlacement(determinationContext),
    freeDownload: getFreeDownloadPlacement(determinationContext),
    sellDeviation: getSellDeviationPlacement(determinationContext),
    deleteDeviation: getDeleteDeviationPlacement(determinationContext),
    editDeviation: getEditDeviationPlacement(determinationContext),
    copyLink: getCopyLinkPlacement(determinationContext),
    muroImport: getMuroImportPlacement(determinationContext),
    muroOpen: getMuroOpenPlacement(determinationContext),
    createVideo: getCreateVideoPlacement(determinationContext),
    elementGroups: elementGroups,
    meta: {
      hasPrimaryMoreActionsMenu: false,
    },
  };

  placementMap.meta = {
    // ElementPlacementMap.elementGroups has no controls that appear in menus for the time being, so we're
    // only testing the root values of ElementPlacementMap.
    hasPrimaryMoreActionsMenu: hasElementInTargetMoreMenu(placementMap),
  };

  return placementMap;
}

function getSellDeviationPlacement(
  determinationContext: DeterminationContext
): StashDeviationElementPlacementMap['sellDeviation'] {
  const { deviationAttributes } = determinationContext;

  if (!deviationAttributes.isSellable) {
    return StashDeviationElementPlacement.NotIncluded;
  }

  return StashDeviationElementPlacement.PrimaryBar;
}

function getDeleteDeviationPlacement(
  determinationContext: DeterminationContext
): StashDeviationElementPlacementMap['deleteDeviation'] {
  return determinationContext.deviationAttributes.isDeletable
    ? StashDeviationElementPlacement.PrimaryBarMenu
    : StashDeviationElementPlacement.NotIncluded;
}

function getEditDeviationPlacement(
  determinationContext: DeterminationContext
): StashDeviationElementPlacementMap['editDeviation'] {
  const { deviation, deviationAttributes } = determinationContext;

  // Let backend hide the edit button, move into the helper if we
  // need to disable outside of stash
  if (
    deviation.legacyTextEditUrl === '#' ||
    (deviation.extended && deviation.extended.editUrl === '#')
  ) {
    return StashDeviationElementPlacement.NotIncluded;
  }

  return deviationAttributes.isEditable
    ? StashDeviationElementPlacement.PrimaryBar
    : StashDeviationElementPlacement.NotIncluded;
}

function getCopyLinkPlacement(
  determinationContext: DeterminationContext
): StashDeviationElementPlacementMap['copyLink'] {
  const { isAuthor, deviationAttributes, elementGroups } = determinationContext;

  if (
    shouldCollapseForAdoptableElementGroups(determinationContext) ||
    deviationAttributes.isSellable ||
    !isAuthor ||
    // In addition to the usual collapsing for adoptable element groups, we want to always collapse this element into
    // the menu if we're the original author + it's been listed for sale.
    (isAuthor &&
      elementGroups.adoptableSeller.groupPlacement ===
        StashDeviationElementPlacement.PrimaryBar)
  ) {
    return StashDeviationElementPlacement.PrimaryBarMenu;
  }

  return StashDeviationElementPlacement.PrimaryBar;
}

function getMuroImportPlacement(
  determinationContext: DeterminationContext
): StashDeviationElementPlacementMap['muroImport'] {
  const { deviation } = determinationContext;
  return !deviation.extended?.muroImportUrl
    ? StashDeviationElementPlacement.NotIncluded
    : StashDeviationElementPlacement.PrimaryBarMenu;
}

function getMuroOpenPlacement(
  determinationContext: DeterminationContext
): StashDeviationElementPlacementMap['muroOpen'] {
  const { deviation, isAuthor } = determinationContext;

  if (isAuthor && isSafeForMuroAndVideoCreator(deviation)) {
    return StashDeviationElementPlacement.PrimaryBarMenu;
  }

  return StashDeviationElementPlacement.NotIncluded;
}

function getFreeDownloadPlacement(
  determinationContext: DeterminationContext
): StashDeviationElementPlacementMap['freeDownload'] {
  const {
    isAuthor,
    isMobile,
    deviationAttributes: { isDownloadable },
  } = determinationContext;

  if (!isDownloadable) {
    return StashDeviationElementPlacement.NotIncluded;
  }

  if (isMobile && !isAuthor) {
    return StashDeviationElementPlacement.PrimaryBar;
  }

  if (
    shouldCollapseForAdoptableElementGroups(determinationContext) ||
    isAuthor
  ) {
    return StashDeviationElementPlacement.PrimaryBarMenu;
  }

  return StashDeviationElementPlacement.PrimaryBar;
}

function getPremiumDownloadPlacement(
  determinationContext: DeterminationContext
): StashDeviationElementPlacementMap['premiumDownload'] {
  const {
    deviationAttributes: { isPurchasable },
  } = determinationContext;

  if (!isPurchasable) {
    return StashDeviationElementPlacement.NotIncluded;
  }

  return StashDeviationElementPlacement.PrimaryBar;
}

function getCreateVideoPlacement(
  determinationContext: DeterminationContext
): StashDeviationElementPlacementMap['createVideo'] {
  const { flags, isAuthor, deviation } = determinationContext;
  if (
    !flags.includes('video_creator') ||
    !isAuthor ||
    !isSafeForMuroAndVideoCreator(deviation)
  ) {
    return StashDeviationElementPlacement.NotIncluded;
  }
  return StashDeviationElementPlacement.PrimaryBarMenu;
}

/**
 * This is useful for determining if we should include the more menu button at all. Prevents downstream consumers from
 * having to do it themselves.
 */
function hasElementInTargetMoreMenu(
  elementPlacementMap: StashDeviationElementPlacementMap
): boolean {
  const elementPlacementValues = Object.values(elementPlacementMap);

  for (let i = 0; i < elementPlacementValues.length; i++) {
    const currentPlacementValue = elementPlacementValues[i];
    if (
      isArray(currentPlacementValue) &&
      currentPlacementValue.includes(
        StashDeviationElementPlacement.PrimaryBarMenu
      )
    ) {
      return true;
    } else if (
      currentPlacementValue === StashDeviationElementPlacement.PrimaryBarMenu
    ) {
      return true;
    }
  }

  return false;
}
